import styled from '@emotion/styled';
import { forwardRef, Ref, useEffect } from 'react';
import useForwardedRef from '../../common/hooks/useForwardedRef';
import { SPACING } from '../../common/styles/spacing';
import isRateLimited from '../../common/utils/isRateLimited';
import type { RateResults as RateResultsType } from './PublicRatesCalculatorForm';
import RateCard, { UpsCreateAccountRateCard } from './RateCard';
import RateResults from './RateResults';

const Styled = {
  RateResultsWrapper: styled.div`
    padding: 0 ${SPACING.sm};
  `,
};

export type PublicRatesCalculatorResultsProps = {
  rateResults: RateResultsType;
  scrollOnResultsChange?: boolean;
  onRegisterClick: () => void;
};

function PublicRatesCalculatorRateResultsComponent(
  { rateResults, scrollOnResultsChange, onRegisterClick }: PublicRatesCalculatorResultsProps,
  ref: Ref<HTMLDivElement>,
) {
  const forwardedRef = useForwardedRef(ref);

  // Scroll to results when they change
  useEffect(() => {
    if (scrollOnResultsChange && forwardedRef.current) {
      forwardedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [scrollOnResultsChange, rateResults, forwardedRef]);

  // Check if UPS rating is rate limited
  const isUpsRateLimited = isRateLimited(rateResults.errors);

  return (
    <Styled.RateResultsWrapper>
      <RateResults ref={forwardedRef} errors={rateResults.errors}>
        {rateResults.data?.rates?.map((rate) => (
          <RateCard
            key={`${rate.mailClassKey}-${rate.packageTypeKey}`}
            rate={rate}
            onButtonClick={onRegisterClick}
          />
        ))}

        {isUpsRateLimited && (
          <UpsCreateAccountRateCard
            onRegisterLinkClick={onRegisterClick}
            onButtonClick={onRegisterClick}
          />
        )}
      </RateResults>
    </Styled.RateResultsWrapper>
  );
}

const PublicRatesCalculatorRateResults = forwardRef(PublicRatesCalculatorRateResultsComponent);
export default PublicRatesCalculatorRateResults;
